import React, { useMemo, useState } from "react";
import { Field } from "formik";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import MuiSelect from "@mui/material/Select";
import ListSubheader from "@mui/material/ListSubheader";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const styles = {
  hiddenMenuItem: { display: "none" },
  menuItem: {
    paddingLeft: "32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  menuItemContent: {
    flex: 1,
  },
  infoButton: {
    padding: "6px",
    marginLeft: "8px",
    color: "#666",
    "&:hover": {
      color: "#333",
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  tooltip: {
    fontSize: "14px",
    lineHeight: "1.4",
  },
  subheader: subheaderFontSize => ({
    fontSize: subheaderFontSize,
    cursor: "pointer",
    pointerEvents: "auto",
    backgroundColor: "#fafafa",
  }),
};

const CollapsibleSelect = ({
  options = [],
  label,
  InputLabelProps,
  size,
  required,
  subheaderFontSize = "0.875rem",
  ...props
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = label => {
    setExpandedGroups(state => ({ ...state, [label]: !state[label] }));
  };

  const valueToLabel = useMemo(() => {
    const map = new Map();
    options.forEach(option => {
      if (Array.isArray(option)) {
        const [value, optionLabel] = option;
        map.set(value, optionLabel);
        return;
      }
      if (option && typeof option === "object" && !option.subheader) {
        map.set(option.value, option.label);
      }
    });
    return map;
  }, [options]);

  return (
    <FormControl fullWidth variant="outlined" size={size} required={required}>
      <Field {...props}>
        {({ field, form, meta }) => {
          const error = (meta.touched || form.submitCount > 0) && meta.error;

          let currentGroup = null;
          let isCurrentGroupCollapsed = true;

          return (
            <>
              <InputLabel id={`select-label-${field.name}`} error={!!error} {...InputLabelProps}>
                {label}
              </InputLabel>
              <MuiSelect
                {...props}
                data-id={field.name}
                labelId={`select-label-${field.name}`}
                label={label}
                id={field.name}
                value={field.value || ""}
                error={!!error}
                disabled={props.disabled}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                onChange={value => form.setFieldValue(field.name, value.target.value)}
                renderValue={selected => {
                  if (!selected) return "";
                  const labelForValue = valueToLabel.get(selected);
                  return labelForValue || "";
                }}
              >
                <MenuItem value="" style={styles.hiddenMenuItem} />
                {options.map((option, idx) => {
                  if (Array.isArray(option)) {
                    const [value, optionLabel] = option;
                    if (currentGroup && isCurrentGroupCollapsed) {
                      if (value === field.value) {
                        return (
                          <MenuItem key={value} value={value} style={styles.hiddenMenuItem}>
                            {optionLabel}
                          </MenuItem>
                        );
                      }
                      return null;
                    }
                    return (
                      <MenuItem
                        key={value}
                        value={value}
                        data-id="select-item"
                        style={styles.menuItem}
                      >
                        {optionLabel}
                      </MenuItem>
                    );
                  }

                  const {
                    value,
                    label: optionLabel,
                    disabled = false,
                    subheader = false,
                    tooltip,
                  } = option;

                  if (subheader) {
                    currentGroup = optionLabel;
                    isCurrentGroupCollapsed = !expandedGroups[optionLabel];
                    return (
                      <ListSubheader
                        key={`subheader-${idx}`}
                        disableSticky
                        component="div"
                        role="button"
                        tabIndex={-1}
                        style={styles.subheader(subheaderFontSize)}
                        onMouseDown={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleGroup(optionLabel);
                        }}
                        onClickCapture={e => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <span>{optionLabel}</span>
                          {isCurrentGroupCollapsed ? (
                            <ChevronRightIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </Box>
                      </ListSubheader>
                    );
                  }

                  if (currentGroup && isCurrentGroupCollapsed) {
                    if (value === field.value) {
                      return (
                        <MenuItem key={value} value={value} style={styles.hiddenMenuItem}>
                          {optionLabel}
                        </MenuItem>
                      );
                    }
                    return null;
                  }

                  return (
                    <MenuItem
                      key={value}
                      value={value}
                      disabled={disabled}
                      data-id="select-item"
                      style={styles.menuItem}
                    >
                      <span style={styles.menuItemContent}>{optionLabel}</span>
                      {tooltip && (
                        <Tooltip
                          title={tooltip}
                          arrow
                          placement="right"
                          componentsProps={{
                            tooltip: {
                              sx: styles.tooltip,
                            },
                          }}
                        >
                          <IconButton
                            size="medium"
                            style={styles.infoButton}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                          >
                            <InfoOutlinedIcon fontSize="medium" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </MenuItem>
                  );
                })}
              </MuiSelect>

              {error && (
                <FormHelperText htmlFor={field.name} error={!!error}>
                  {error}
                </FormHelperText>
              )}
            </>
          );
        }}
      </Field>
    </FormControl>
  );
};

export default CollapsibleSelect;
